import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Star, UserPlus } from 'lucide-react';
import { ticketingApi, type Vendor } from '@/lib/ticketing-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { StatusPill, KpiStat } from '@/components/ui/primitives';
import { QuickFormModal } from '@/components/QuickFormModal';
import { useToast } from '@/components/ui/Toast';

// §24 — derive a vendor "type" translation key from its service categories for the profile chip.
const vendorType = (cats?: string[]) => {
  const c = cats ?? [];
  if (c.includes('Access') && (c.includes('CCTV') || c.includes('Alarm'))) return 'vendors.type.systemsIntegrator';
  if (c.includes('Alarm')) return 'vendors.type.alarmMonitoring';
  if (c.includes('NVR') || c.includes('CCTV')) return 'vendors.type.cctvVideo';
  return 'vendors.type.serviceProvider';
};

export function VendorsPage() {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [invite, setInvite] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [inviteResult, setInviteResult] = useState<{ email: string; created: boolean; emailSent?: boolean } | null>(null);
  const { data: vendors, isLoading } = useQuery({ queryKey: ['vendors'], queryFn: ticketingApi.listVendors });

  const total = vendors?.length ?? 0;
  const activeCount = (vendors ?? []).filter((v) => v.status === 'active').length;
  const avgSla = total ? Math.round((vendors ?? []).reduce((s, v) => s + (v.slaCompliance ?? 0), 0) / total) : 0;
  const totalContracts = (vendors ?? []).reduce((s, v) => s + (v.activeContracts ?? 0), 0);

  const columns: Column<Vendor>[] = [
    { key: 'name', header: t('vendors.columns.name'), render: (v) => (
      <span className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-primary/15 text-primary"><Building2 size={14} /></span>
        <span className="font-medium">{v.name}</span>
      </span>
    ) },
    { key: 'type', header: t('vendors.columns.type'), render: (v) => <span className="text-muted">{t(vendorType(v.categories))}</span> },
    { key: 'partnerLevel', header: t('vendors.columns.partner'), render: (v) => <span className="capitalize text-muted">{v.partnerLevel ?? '—'}</span> },
    { key: 'status', header: t('vendors.columns.status'), render: (v) => <StatusPill status={v.status} /> },
    { key: 'rating', header: t('vendors.columns.rating'), render: (v) => (
      <span className="flex items-center gap-0.5"><Star size={13} className="fill-warning text-warning" /> <span className="tk-num">{(v.rating ?? 0).toFixed(1)}</span></span>
    ) },
    { key: 'categories', header: t('vendors.columns.categories'), render: (v) => {
      const cats = v.categories ?? [];
      const extra = cats.length - 3;
      return (
        <span className="flex flex-wrap gap-1">
          {cats.slice(0, 3).map((c) => <span key={c} className="rounded-full bg-white/5 px-1.5 py-0.5 text-[11px] text-muted ring-1 ring-white/10">{c}</span>)}
          {extra > 0 && <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[11px] text-muted ring-1 ring-white/10" title={cats.slice(3).join(', ')}>+{extra}</span>}
        </span>
      );
    } },
    { key: 'activeContracts', header: t('vendors.columns.contracts'), render: (v) => <span className="tk-num">{v.activeContracts ?? 0}</span> },
    { key: 'avgResolutionHours', header: t('vendors.columns.avgResolve'), render: (v) => <span className="tk-num">{v.avgResolutionHours != null ? `${v.avgResolutionHours}h` : '—'}</span> },
    { key: 'slaCompliance', header: t('vendors.columns.sla'), render: (v) => <span className={`tk-num ${(v.slaCompliance ?? 0) >= 90 ? 'text-ok' : 'text-warning'}`}>{v.slaCompliance != null ? `${v.slaCompliance}%` : '—'}</span> },
    { key: 'notificationEmail', header: t('vendors.columns.email'), render: (v) => <span className="text-xs text-muted">{v.notificationEmail ?? '—'}</span> },
    { key: 'phone', header: t('vendors.columns.phone'), render: (v) => <span className="text-xs text-muted">{v.phone ?? '—'}</span> },
    { key: 'certifications', header: t('vendors.columns.certs'), render: (v) => <span className="text-xs text-muted">{(v.certifications ?? []).join(', ') || '—'}</span> },
    { key: 'invite', header: '', render: (v) => (
      <button className="tk-btn-ghost px-2 py-1 text-xs" onClick={(e) => { e.stopPropagation(); setInvite({ id: v.id, name: v.name, email: v.notificationEmail }); }}>
        <UserPlus size={13} /> {t('vendors.actions.invite')}
      </button>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vendors.title')}
        subtitle={t('vendors.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('vendors.actions.addVendor')}</button>}
      />

      {addOpen && (
        <QuickFormModal
          title={t('vendors.addModal.title')}
          submitLabel={t('vendors.addModal.submit')}
          fields={[
            { key: 'name', label: t('vendors.addModal.fields.name'), required: true, placeholder: t('vendors.addModal.fields.namePlaceholder') },
            { key: 'notificationEmail', label: t('vendors.addModal.fields.email'), type: 'email', placeholder: t('vendors.addModal.fields.emailPlaceholder') },
            { key: 'status', label: t('vendors.addModal.fields.status'), type: 'select', defaultValue: 'active', options: [
              { value: 'active', label: t('vendors.status.active') }, { value: 'under-review', label: t('vendors.status.underReview') }, { value: 'inactive', label: t('vendors.status.inactive') },
            ] },
            { key: 'partnerLevel', label: t('vendors.addModal.fields.partnerLevel'), type: 'select', options: [
              { value: 'preferred', label: t('vendors.partner.preferred') }, { value: 'standard', label: t('vendors.partner.standard') }, { value: 'new', label: t('vendors.partner.new') },
            ] },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            await ticketingApi.createVendor({ name: v.name, notificationEmail: v.notificationEmail || undefined, status: v.status || 'active', partnerLevel: v.partnerLevel || undefined });
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            toast.success(t('vendors.toast.added', { name: v.name }));
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('vendors.kpi.vendors')} value={total} tone="info" />
        <KpiStat label={t('vendors.kpi.active')} value={activeCount} tone="ok" />
        <KpiStat label={t('vendors.kpi.avgSla')} value={`${avgSla}%`} tone={avgSla >= 90 ? 'ok' : 'warning'} />
        <KpiStat label={t('vendors.kpi.activeContracts')} value={totalContracts} tone="info" />
      </div>

      <DataTable
        columns={columns}
        rows={vendors ?? []}
        loading={isLoading}
        emptyIcon={Building2}
        emptyTitle={t('vendors.empty.title')}
        emptyHint={t('vendors.empty.hint')}
      />

      {invite && (
        <QuickFormModal
          title={t('vendors.inviteModal.title', { name: invite.name })}
          submitLabel={t('vendors.inviteModal.submit')}
          fields={[
            { key: 'email', label: t('vendors.inviteModal.fields.email'), type: 'email', required: true, defaultValue: invite.email ?? '' },
          ]}
          onClose={() => setInvite(null)}
          onSubmit={async (vals) => {
            const r = await ticketingApi.inviteVendorToPortal(invite.id, { email: vals.email, name: invite.name });
            setInviteResult({ email: r.email, created: r.created, emailSent: r.emailSent });
          }}
        />
      )}

      {inviteResult && (
        <div className="tk-modal-overlay" onClick={() => setInviteResult(null)}>
          <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 text-base font-semibold">{t('vendors.inviteResult.title')}</div>
            <p className="text-sm text-muted">{t('vendors.inviteResult.linkedBefore', { email: inviteResult.email })}<span className="tk-num">/vendor</span>{t('vendors.inviteResult.linkedAfter')}</p>
            {inviteResult.created && (
              <div className="mt-3 rounded-md bg-ok/10 p-3 text-sm text-muted ring-1 ring-ok/30">
                {inviteResult.emailSent === false
                  ? t('vendors.inviteResult.emailFailed', { defaultValue: "We couldn't send the invite email automatically. Ask the vendor to check spam, or resend the invitation." })
                  : t('vendors.inviteResult.emailSent', { defaultValue: 'An invitation email was sent with a secure link to set their password (expires in 3 days).' })}
              </div>
            )}
            {!inviteResult.created && <p className="mt-2 text-xs text-muted">{t('vendors.inviteResult.existingAccount')}</p>}
            <button className="tk-btn-primary mt-4 w-full justify-center" onClick={() => setInviteResult(null)}>{t('vendors.inviteResult.done')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

